import React from "react";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import {
  Building2,
  ChevronDown,
  CircleHelp,
  Home,
  Search,
  ShieldCheck,
  Sparkles,
  Wrench,
  X,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import "./FaqSection.css";

/* =========================================================*

*   CATEGORIES*

*   ========================================================= */

const FAQ_CATEGORIES = [
  {
    key: "general",

    Icon: CircleHelp,
  },

  {
    key: "homeowner",

    Icon: Home,
  },

  {
    key: "oss",

    Icon: Wrench,
  },

  {
    key: "lender",

    Icon: Building2,
  },

  {
    key: "admin",

    Icon: ShieldCheck,
  },
];

/* =========================================================*

*   FAQ ITEMS*

*   ========================================================= */

const FAQ_ITEMS = {
  general: [
    "whatIsLegup",

    "whoCanUse",

    "sharedDossier",

    "platformRole",

    "documentsCommunication",
  ],

  homeowner: [
    "homeownerDossier",

    "homeownerConnections",

    "homeownerDocuments",

    "homeownerRenovation",

    "homeownerInvoices",
  ],

  oss: [
    "ossJoining",

    "ossServicesRegions",

    "ossRequests",

    "ossRenovationPlan",

    "ossCommunication",
  ],

  lender: [
    "lenderCases",

    "lenderReview",

    "lenderRenovationDecision",

    "lenderFundingSources",

    "lenderCommunication",
  ],

  admin: [
    "adminAccess",

    "adminResponsibilities",

    "adminOrganisations",

    "adminRegions",

    "adminOversight",
  ],
};

export default function FaqSection() {
  const { t } = useTranslation("home");

  const prefersReducedMotion = useReducedMotion();

  /* =========================================================*

*     STATE*

*     ========================================================= */

  const [activeCategory, setActiveCategory] = React.useState("general");

  const [openQuestion, setOpenQuestion] = React.useState(null);

  const [searchQuery, setSearchQuery] = React.useState("");

  /* =========================================================*

*     TRANSLATED ITEMS*

*     ========================================================= */

  const allItems = React.useMemo(() => {
    return Object.entries(FAQ_ITEMS).flatMap(([category, ids]) =>
      ids.map((id) => ({
        id,

        category,

        question: t(`faq.items.${id}.question`),

        answer: t(`faq.items.${id}.answer`),
      })),
    );
  }, [t]);

  /* =========================================================*

*     FILTER*

*     ========================================================= */

  const visibleItems = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    /**

*     * Normal category mode.*

*     */

    if (!query) {
      return allItems.filter((item) => item.category === activeCategory);
    }

    /**

*     * Search across all categories.*

*     */

    return allItems.filter(
      (item) =>
        item.question.toLowerCase().includes(query) ||
        item.answer.toLowerCase().includes(query),
    );
  }, [activeCategory, allItems, searchQuery]);

  /* =========================================================*

*     ACTIONS*

*     ========================================================= */

  const selectCategory = (category) => {
    setActiveCategory(category);

    setSearchQuery("");

    setOpenQuestion(null);
  };

  const toggleQuestion = (id) => {
    setOpenQuestion((current) => (current === id ? null : id));
  };

  /* =========================================================*

*     INITIAL PAGE REVEAL*



*     Used only for the top FAQ area:*

*     hero / search / filters.*



*     These reveal once.*

*     ========================================================= */

  const topRevealVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : 24,
    },

    visible: {
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.65,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  /* =========================================================*

*     LIVE VIEWPORT SLIDE*



*     This is intentionally NOT once:true.*



*     Therefore:*

*     - enters viewport  -> slides in*

*     - exits viewport   -> slides out*

*     - comes back       -> slides in again*

*     ========================================================= */

  const viewportSlideVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : 36,
    },

    visible: {
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.62,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  /* =========================================================*

*     QUESTION SLIDE*



*     Slight stagger depending on question position.*

*     ========================================================= */

  const questionViewportVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : 34,
    },

    visible: (index = 0) => ({
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.55,

        delay: prefersReducedMotion ? 0 : Math.min(index * 0.045, 0.16),

        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  /* =========================================================*

*     VIEWPORT SETTINGS*

*     ========================================================= */

  const liveViewport = {
    once: false,

    amount: 0.18,

    margin: "-4% 0px -4% 0px",
  };

  /* =========================================================*

*     RENDER*

*     ========================================================= */

  return (
    <section
      className="faq-section"
      aria-labelledby="faq-title"
      style={{ overflowAnchor: "none" }}
    >
      {/* =====================================================*

*          BACKGROUND*

*          ===================================================== */}

      <div className="faq-section__background" aria-hidden="true">
        <div
          className="

            faq-section__glow

            faq-section__glow--one

          "
        />

        <div
          className="

            faq-section__glow

            faq-section__glow--two

          "
        />

        <div className="faq-section__grid" />
      </div>

      <div className="faq-section__inner">
        {/* ===================================================*

*            HERO*

*            =================================================== */}

        <motion.header
          className="faq-section__header"
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,

            amount: 0.1,
          }}
          variants={topRevealVariants}
        >
          <motion.h1
            id="faq-title"
            className="

              faq-section__title

              faq-section__title--single-line

            "
            variants={topRevealVariants}
          >
            <span className="faq-section__title-prefix">
              {t("faq.title.prefix", "Questions about")}
            </span>{" "}
            <strong>{t("faq.title.highlight", "LEG-UP?")}</strong>
          </motion.h1>

          <motion.p
            className="

              faq-section__lead

              faq-section__lead--single-line

            "
            variants={topRevealVariants}
          >
            {t(
              "faq.description",

              "Find answers about the LEG-UP platform, the digital dossier, renovation support, financing and the workflows available to each platform user.",
            )}
          </motion.p>
        </motion.header>

        {/* ===================================================*

*            SEARCH*

*            =================================================== */}

        <motion.div
          className="faq-search"
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,

            amount: 0.15,
          }}
          variants={topRevealVariants}
        >
          <Search className="faq-search__icon" size={21} strokeWidth={1.9} />

          <input
            type="search"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);

              setOpenQuestion(null);
            }}
            placeholder={t(
              "faq.search.placeholder",

              "Search questions and answers...",
            )}
            aria-label={t(
              "faq.search.label",

              "Search frequently asked questions",
            )}
          />

          <AnimatePresence>
            {searchQuery ? (
              <motion.button
                type="button"
                className="faq-search__clear"
                onClick={() => {
                  setSearchQuery("");

                  setOpenQuestion(null);
                }}
                initial={{
                  opacity: 0,

                  scale: 0.8,
                }}
                animate={{
                  opacity: 1,

                  scale: 1,
                }}
                exit={{
                  opacity: 0,

                  scale: 0.8,
                }}
                aria-label={t("faq.search.clear", "Clear search")}
              >
                <X size={18} />
              </motion.button>
            ) : null}
          </AnimatePresence>
        </motion.div>

        {/* ===================================================*

*            CATEGORY FILTERS*

*            =================================================== */}

        <motion.nav
          className="faq-categories"
          aria-label={t("faq.categories.label", "FAQ categories")}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,

            amount: 0.15,
          }}
          variants={topRevealVariants}
        >
          {FAQ_CATEGORIES.map(({ key, Icon }) => {
            const active = !searchQuery && activeCategory === key;

            return (
              <motion.button
                key={key}
                type="button"
                className={[
                  "faq-category",

                  active ? "faq-category--active" : "",
                ]

                  .filter(Boolean)

                  .join(" ")}
                onClick={() => selectCategory(key)}
                whileHover={
                  prefersReducedMotion
                    ? undefined
                    : {
                        y: -2,
                      }
                }
                whileTap={
                  prefersReducedMotion
                    ? undefined
                    : {
                        scale: 0.98,
                      }
                }
              >
                {active ? (
                  <motion.span
                    layoutId="faq-category-active"
                    className="faq-category__active-background"
                    transition={{
                      type: "spring",

                      stiffness: 420,

                      damping: 35,
                    }}
                  />
                ) : null}

                <span className="faq-category__content">
                  <Icon size={19} strokeWidth={1.9} />

                  <span>{t(`faq.categories.${key}`)}</span>

                  <span className="faq-category__count">
                    {FAQ_ITEMS[key].length}
                  </span>
                </span>
              </motion.button>
            );
          })}
        </motion.nav>

        {/* ===================================================*

*            FAQ CONTENT*

*            =================================================== */}

        <div className="faq-section__content">
          {/* =================================================*

*              CATEGORY TEXT*



*              Slides in when entering.*

*              Slides out when leaving.*

*              ================================================= */}

          <AnimatePresence mode="wait">
            <motion.div
              key={searchQuery ? `search-${searchQuery}` : activeCategory}
              className="faq-section__category-heading"
              initial="hidden"
              whileInView="visible"
              viewport={liveViewport}
              variants={viewportSlideVariants}
              exit={{
                opacity: 0,

                y: prefersReducedMotion ? 0 : -18,

                transition: {
                  duration: prefersReducedMotion ? 0.01 : 0.25,
                },
              }}
            >
              <span className="faq-section__category-label">
                {searchQuery
                  ? t("faq.search.results", "SEARCH RESULTS")
                  : t(`faq.categoryDescriptions.${activeCategory}.eyebrow`)}
              </span>

              <h2>
                {searchQuery
                  ? t("faq.search.title", {
                      count: visibleItems.length,

                      defaultValue: "{{count}} matching questions",
                    })
                  : t(`faq.categoryDescriptions.${activeCategory}.title`)}
              </h2>

              {!searchQuery ? (
                <p>
                  {t(`faq.categoryDescriptions.${activeCategory}.description`)}
                </p>
              ) : null}
            </motion.div>
          </AnimatePresence>

          {/* =================================================*

*              QUESTIONS*



*              Every card has its OWN viewport observer.*

*              ================================================= */}

          <motion.div className="faq-list">
            <AnimatePresence mode="popLayout">
              {visibleItems.map((item, index) => {
                const open = openQuestion === item.id;

                const category = FAQ_CATEGORIES.find(
                  (candidate) => candidate.key === item.category,
                );

                const CategoryIcon = category?.Icon || CircleHelp;

                return (
                  <motion.article
                    key={item.id}
                    custom={index}
                    layout
                    className={["faq-item", open ? "faq-item--open" : ""]

                      .filter(Boolean)

                      .join(" ")}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                      once: false,

                      amount: 0.01,

                      margin: "180px 0px 180px 0px",
                    }}
                    variants={questionViewportVariants}
                    exit={{
                      opacity: 0,

                      y: prefersReducedMotion ? 0 : -22,

                      transition: {
                        duration: prefersReducedMotion ? 0.01 : 0.26,
                      },
                    }}
                  >
                    {/* =======================================*

*                          QUESTION BUTTON*

*                          ======================================= */}

                    <button
                      type="button"
                      className="faq-item__question"
                      onClick={() => toggleQuestion(item.id)}
                      aria-expanded={open}
                      aria-controls={`faq-answer-${item.id}`}
                    >
                      <span className="faq-item__question-left">
                        {searchQuery ? (
                          <span className="faq-item__category-icon">
                            <CategoryIcon size={18} strokeWidth={1.9} />
                          </span>
                        ) : null}

                        <span className="faq-item__number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="faq-item__question-text">
                          {item.question}
                        </span>
                      </span>

                      <motion.span
                        className="faq-item__toggle"
                        animate={{
                          rotate: open ? 180 : 0,
                        }}
                        transition={{
                          duration: prefersReducedMotion ? 0.01 : 0.28,
                        }}
                      >
                        <ChevronDown size={21} strokeWidth={2} />
                      </motion.span>
                    </button>

                    {/* =======================================*

*                          ANSWER*



*                          This animation stays separate from*

*                          viewport animation.*

*                          ======================================= */}

                    <AnimatePresence initial={false}>
                      {open ? (
                        <motion.div
                          id={`faq-answer-${item.id}`}
                          className="faq-item__answer-wrap"
                          initial={{
                            height: 0,

                            opacity: 0,
                          }}
                          animate={{
                            height: "auto",

                            opacity: 1,
                          }}
                          exit={{
                            height: 0,

                            opacity: 0,
                          }}
                          transition={{
                            height: {
                              duration: prefersReducedMotion ? 0.01 : 0.38,

                              ease: [0.22, 1, 0.36, 1],
                            },

                            opacity: {
                              duration: prefersReducedMotion ? 0.01 : 0.25,
                            },
                          }}
                          style={{ overflow: "hidden" }}
                        >
                          <div className="faq-item__answer">
                            <div className="faq-item__answer-line" />

                            <p>{item.answer}</p>
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </motion.article>
                );
              })}
            </AnimatePresence>

            {/* =================================================*

*                EMPTY SEARCH*

*                ================================================= */}

            {visibleItems.length === 0 ? (
              <motion.div
                className="faq-empty"
                initial="hidden"
                whileInView="visible"
                viewport={liveViewport}
                variants={viewportSlideVariants}
              >
                <CircleHelp size={31} strokeWidth={1.8} />

                <h3>{t("faq.empty.title", "No matching questions")}</h3>

                <p>
                  {t(
                    "faq.empty.description",

                    "Try another keyword or browse one of the FAQ categories.",
                  )}
                </p>
              </motion.div>
            ) : null}
          </motion.div>
        </div>

        {/* ===================================================*

*            HELP PANEL*



*            Slides in from below on entry.*

*            Slides back down when it leaves viewport.*

*            =================================================== */}

        <motion.div
          className="faq-help"
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: false,

            amount: 0.22,

            margin: "-4% 0px -4% 0px",
          }}
          variants={{
            hidden: {
              opacity: 0,

              y: prefersReducedMotion ? 0 : 44,
            },

            visible: {
              opacity: 1,

              y: 0,

              transition: {
                duration: prefersReducedMotion ? 0.01 : 0.7,

                ease: [0.22, 1, 0.36, 1],
              },
            },
          }}
        >
          <div className="faq-help__icon">
            <CircleHelp size={27} strokeWidth={1.9} />
          </div>

          <div className="faq-help__copy">
            <motion.span
              initial={{
                opacity: 0,

                y: prefersReducedMotion ? 0 : 14,
              }}
              whileInView={{
                opacity: 1,

                y: 0,
              }}
              viewport={{
                once: false,

                amount: 0.4,
              }}
              transition={{
                duration: prefersReducedMotion ? 0.01 : 0.45,

                delay: prefersReducedMotion ? 0 : 0.08,
              }}
            >
              {t("faq.help.eyebrow", "NEED MORE HELP?")}
            </motion.span>

            <motion.h2
              initial={{
                opacity: 0,

                y: prefersReducedMotion ? 0 : 18,
              }}
              whileInView={{
                opacity: 1,

                y: 0,
              }}
              viewport={{
                once: false,

                amount: 0.35,
              }}
              transition={{
                duration: prefersReducedMotion ? 0.01 : 0.5,

                delay: prefersReducedMotion ? 0 : 0.14,

                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {t(
                "faq.help.title",

                "Your LEG-UP journey may be specific to your project.",
              )}
            </motion.h2>

            <motion.p
              initial={{
                opacity: 0,

                y: prefersReducedMotion ? 0 : 18,
              }}
              whileInView={{
                opacity: 1,

                y: 0,
              }}
              viewport={{
                once: false,

                amount: 0.35,
              }}
              transition={{
                duration: prefersReducedMotion ? 0.01 : 0.5,

                delay: prefersReducedMotion ? 0 : 0.2,

                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {t(
                "faq.help.description",

                "Requirements and available services may vary depending on your role, organisation and pilot market. Use the communication tools available in the platform when you need support on a specific case.",
              )}
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
